<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { computed, onMounted, ref, watch } from 'vue';

import { useAuthStore } from '@/stores/authStore.ts';
import MandateService from '@/services/mandate/mandateService.ts';
import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import {
  formatCurrency,
  formatDate,
  formatItemValue,
  formatMandatePublicStatus,
  formatMandateBoStatus
} from '@/utils/formatters.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import {
  MandateBoStatusEnum,
  MandatePublicStatusEnum
} from "@/enums/mandate_enum.ts";

import type {
  MandateList,
  MandateSortKey
} from '@/types/services/mandate/mandate.type.ts';
import type {
  PaginatedQuery,
  SortItem,
  SortOrder
} from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';
import EditMandateFeesModal from '@/views/mandates/admin/EditMandateFeesModal.vue';
import ImportWizard from '@/views/mandates/dam/trackRecord/ImportWizard.vue';
import { useDrawer } from '@/stores/useDrawer.ts';

const { t } = useI18n()
const { openDrawer } = useDrawer()
const router = useRouter()
const authStore = useAuthStore()

const mandates = ref<MandateList[]>([])
const loading = ref<boolean>(false)

const search = ref<string>('')
const sort = ref<MandateSortKey>('createdAt')
const order = ref<SortOrder>('desc')
const limit = ref<number>(10)
const page = ref<number>(1)

const total = ref<number>(0)
const maxPages = ref<number>(1)

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('views.mandatesList.header.author'),
    key: 'author',
    sortable: true,
    align: 'start'

  },
  {
    title: t('views.mandatesList.header.name'),
    key: 'name',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.boStatus'),
    key: 'boStatus',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.publicStatus'),
    key: 'publicStatus',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.createdAt'),
    key: 'createdAt',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.availableAt'),
    key: 'availableAt',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.totalAmountInvested'),
    key: 'totalAmountInvested',
    sortable: false,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.appliedFees'),
    key: 'appliedFees',
    sortable: false,
    align: 'start'
  },
  {
    title: t('views.mandatesList.header.actions'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  },
])

onMounted(fetchMandates);

watch([search, sort, order, limit, page], fetchMandates);

function canSeeDetail(mandate: MandateList) {
  return (
    authStore.isAdmin || (
      mandate.publicStatus === MandatePublicStatusEnum.PRIVATE ||
      mandate.publicStatus === MandatePublicStatusEnum.OPENED
    )
  )
}

function canEdit(mandate: MandateList) {
  return (
    hasPermission(['EDIT_MANDATE']) && mandate.publicStatus !== MandatePublicStatusEnum.CLOSED
  )
}

function canClose(mandate: MandateList) {
  return (
    hasPermission(['CLOSE_MANDATE']) && mandate.publicStatus !== MandatePublicStatusEnum.CLOSED
  )
}

function canTrade(mandate: MandateList) {
  return (
    hasPermission(['TRADE_MANDATE']) && mandate.publicStatus !== MandatePublicStatusEnum.CLOSED
  )
}

function canEditFees(mandate: MandateList) {
  return (hasPermission(['EDIT_PLATFORM_FEE']) && mandate.publicStatus !== MandatePublicStatusEnum.CLOSED)
}

function canImportTrackRecord(mandate: MandateList) {
  return hasPermission(['TRADE_MANDATE']) && mandate.publicStatus !== MandatePublicStatusEnum.CLOSED
}

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function handleCreateMandate(): Promise<void> {
  await router.push("/mandates/create");
}

async function fetchMandates(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedQuery = {
      search: search.value,
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<MandateList> = await MandateService.getPaginatedMandate(params);

    mandates.value = response.data.map((mandate) => ({
      ...mandate,
      totalAmountInvested: mandate.totalAmountInvested,
      appliedFees: mandate.appliedFees
    }));
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<MandateSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function onRowClick(_: MouseEvent, row: { item: MandateList }): Promise<void> {
  if (authStore.isAdmin) {
    await router.push(`/mandates/${row.item.id}/detail`);
  } else {
    if (row.item.publicStatus == MandatePublicStatusEnum.CLOSED) {
      await router.push(`/mandates/${row.item.id}/detail`);
    } else if (row.item.publicStatus == MandatePublicStatusEnum.OPENED
      && row.item.boStatus == MandateBoStatusEnum.VALIDATED
      && hasPermission(['TRADE_MANDATE'])) {
      await router.push(`/mandates/${row.item.id}/trading`);
    } else {
      if (hasPermission(['EDIT_MANDATE'])) {
        await router.push(`/mandates/${row.item.id}`);
      }
    }
  }
}

async function closeMandate(mandate: MandateList): Promise<void> {
  const options: ModalOptions = {
    title: t('views.mandatesList.closeMandate.title'),
    message: t('views.mandatesList.closeMandate.message', { name: `${mandate.name}` }),
  }

  const modal = await displayModal(options)

  if (modal) {
    await MandateService.updateStatus(mandate.id, 'closed')
    displayToast(t('views.mandatesList.closeMandate.success'), 'success')
    await fetchMandates()
  }
}

async function openEditFeesModal(mandate: MandateList): Promise<void> {
  openDrawer({
    title: mandate.name,
    component: EditMandateFeesModal,
    props: {
      key: mandate.id + "-" + Date.now(),
      mandateId: mandate.id,
      drawerMode: true,
      onUpdated: async () => {
        await fetchMandates();
        useDrawer().closeDrawer();
      },
    },
    width: 520,
  });
}

function openTrackRecordWizard(mandate: MandateList): void {
  openDrawer({
    title: `${mandate.name} — ${t('views.trackRecord.title')}`,
    component: ImportWizard,
    props: {
      key: `track-record-${mandate.id}-${Date.now()}`,
      mandateId: mandate.id,
      onClose: () => useDrawer().closeDrawer(),
    },
    width: 680,
  });
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhHandCoins">
    {{ t("views.mandatesList.title") }}
  </MainTitleComponent>

  <v-row justify="space-between" align="center">
    <v-col cols="12" sm="5">
      <v-text-field
        v-model="search"
        :label="t('views.mandatesList.search')"
        append-inner-icon="PhMagnifyingGlass"
        density="compact"
        hide-details
      />
    </v-col>

    <v-col v-if="hasPermission(['ADD_MANDATE'])" cols="12" sm="auto">
      <v-btn
        class="w-100 w-sm-auto"
        @click="handleCreateMandate"
      >
        {{ t("views.mandatesList.buttons.create") }}
        <template #prepend>
          <v-icon icon="PhPlus" />
        </template>
      </v-btn>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="mandates"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.author`]="{ item }">
          {{ formatItemValue(item.author) }}
        </template>
        <template #[`item.name`]="{ item }">
          {{ formatItemValue(item.name) }}
        </template>
        <template #[`item.boStatus`]="{ item }">
          <v-chip
            :color="formatMandateBoStatus(item.boStatus)"
            text-color="white"
            class="ma-2"
          >
            {{ t('views.mandatesList.boStatus.' + item.boStatus) }}
          </v-chip>
        </template>
        <template #[`item.publicStatus`]="{ item }">
          <v-chip
            :color="formatMandatePublicStatus(item.publicStatus)"
            text-color="white"
            class="ma-2"
          >
            {{ t('views.mandatesList.publicStatus.' + item.publicStatus) }}
          </v-chip>
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template #[`item.availableAt`]="{ item }">
          {{ formatDate(item.availableAt) }}
        </template>
        <template #[`item.totalAmountInvested`]="{ item }">
          {{ formatCurrency(item.totalAmountInvested) }}
        </template>
        <template #[`item.appliedFees`]="{ item }">
          {{ formatCurrency(item.appliedFees) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu
            v-if="canSeeDetail(item) || canEdit(item) || canClose(item) || canTrade(item) || canEditFees(item) || canImportTrackRecord(item)"
          >
            <template #activator="{ props }">
              <button type="button" v-bind="props">
                <v-icon icon="PhDotsThreeVertical" />
              </button>
            </template>

            <v-list>
              <v-list-item
                v-if="canSeeDetail(item)"
                @click.prevent="router.push(`/mandates/${item.id}/detail`)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.detailMandate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhEye" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canEdit(item)"
                @click.prevent="router.push(`/mandates/${item.id}`)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.updateMandate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhPencil" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canClose(item)"
                @click.prevent="closeMandate(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.closeMandate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhLock" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canTrade(item)"
                @click.prevent="router.push(`/mandates/${item.id}/trading`)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.tradingMandate') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhChartBar" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canEditFees(item)"
                @click.prevent="openEditFeesModal(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.managementFees') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhCoins" />
                </template>
              </v-list-item>

              <v-list-item
                v-if="canImportTrackRecord(item)"
                @click.prevent="openTrackRecordWizard(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('views.mandatesList.table.actions.importTrackRecord') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhChartLineUp" />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
